import { useToast } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../common/src/utils/globalVariables';
import { hasSubGroups } from '../config/flowsStructureConfig';
import {
  incSubmitCount,
  setIsSessionActive,
  setLastPosition,
} from '../store/redux/slices/flowsMetaDataSlice';
import {
  setToastErrorMessage,
  setValidationErrorMessage,
} from '../store/redux/slices/flowsSlice';
import store from '../store/redux/store';
import { FlowSessionRouteParams } from '../types/RouteParams';
import useCurrentGroup from './useCurrentGroup';
import { useSubmitFlowAnswers } from './useFlowsMutations';
import { getIndexOfAnswerForQuestionId } from '../utils/flowsQuestionsUtils';
import { FlowsQuestionAnswer } from '../types/FlowsTypes';
import { useDebouncedCallback } from 'use-debounce/lib';
import useFlowsHelper from './useFlowsHelper';
import { uniq } from 'lodash';
import { FlowsAPIAnswerError } from '../types/ApiTypes';
import { setQueryParam } from '../../../common/src/hooks/query-params/useAddQueryParam';
import { Userpilot } from 'userpilot';
import { useEffect } from 'react';
import { markFlowAsComplete } from '../../../sales-conversation/src/services/louiseApiService';
import {
  flowIdAskaTravelOffer,
  flowIdCitizinsTravel,
  flowIdShortCheckupCompanyBE,
  flowIdShortCheckupCompanyNL,
} from '../constants/flowIds';

interface FlowsProgression {
  nextQuestion: () => void;
  previousQuestion: () => void;
  saveProgress: () => void;
  saveSingleField: (questionId: string, questionIndex?: number) => void;
  saveSingleFieldDebounced: (
    questionId: string,
    questionIndex?: number,
  ) => void;
  saveFields: (answers: FlowsQuestionAnswer[]) => Promise<void>;
}

interface AnswerObject {
  id: string;
  answer: unknown;
  index: number | undefined;
}

export const useFlowsProgression = (): FlowsProgression => {
  const history = useHistory();
  const {
    flowId,
    sessionId,
    position: positionString,
  } = useParams<FlowSessionRouteParams>();
  const { t } = useTranslation();
  const { search } = useLocation();
  const { handleSubmit } = useFormContext() || {};
  const { getAnswersForGroup } = useFlowsHelper();
  const flowsDispatch = useFlowsDispatch();
  const duplicateQuestionMapping = useFlowsSelector(
    (state) => state.flows.metaData.duplicateQuestionMapping,
  );
  const isViewOnlyMode = useFlowsSelector(
    (state) => state.flows.isViewOnlyModeOn,
  );

  const toast = useToast();

  const position = Number(positionString);
  const currentGroup = useCurrentGroup();

  const { mutateAsync, mutate } = useSubmitFlowAnswers();

  const getIdsForGroup = () => {
    /** When a flow uses the TABLE component, the duplicate questions shouldn't be shown in the flow (but in a table) */
    const noDuplicatedQuestionFlows = [
      flowIdShortCheckupCompanyNL,
      flowIdShortCheckupCompanyBE,
      flowIdAskaTravelOffer,
      flowIdCitizinsTravel,
    ];

    if (
      noDuplicatedQuestionFlows.includes(flowId) &&
      duplicateQuestionMapping
    ) {
      const duplicateQuestions = duplicateQuestionMapping[currentGroup.id];
      if (duplicateQuestions) {
        const groupQuestionIds = currentGroup.questions.map(({ id }) => id);
        const ids = [...groupQuestionIds];
        duplicateQuestions.forEach((duplicateQuestionId) => {
          const answers = getAnswersForGroup(duplicateQuestionId);
          const duplicateQuestionIds = uniq(answers.map(({ id }) => id));
          ids.push(...duplicateQuestionIds);
        });
        return ids;
      }
    }

    if (hasSubGroups(flowId)) {
      const currentGroups = [
        ...(currentGroup.groups
          ?.map(({ questions }) => questions.map(({ id }) => id))
          .flat() || []),
      ];

      // Check if any duplicate questions need to be included in the submission as well
      // If the amount question is included in the current request, it will also include
      // the ids of the duplicate questions that were answered
      if (duplicateQuestionMapping)
        Object.entries(duplicateQuestionMapping).forEach(([key, value]) => {
          if (currentGroups.includes(key))
            value.forEach((duplicateQuestionId) => {
              const answers = getAnswersForGroup(duplicateQuestionId);
              const duplicateQuestionIds = uniq(answers.map(({ id }) => id));
              currentGroups.push(...duplicateQuestionIds);
            });
        });

      return currentGroups;
    }

    return currentGroup.questions.map(({ id }) => id);
  };

  const handleOnFormError = () => {
    const toastId = 'flowsProgressionErrorToast';

    if (!toast.isActive(toastId) && hasSubGroups(flowId))
      toast({
        id: toastId,
        description: t('FLOWS_QUESTIONS_VALIDATION-ERROR_MESSAGE'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
  };

  /**
   * This function retrieves the answers for the current group.
   * It filters the answers based on the current group and prepares them for submission.
   *
   * Hidden questions with default values are included:
   * - On the final submission of the entire flow
   * - At the end of each index iteration (e.g., completing car #1, car #2, etc.)
   * This avoids sending them on every intermediate step within an iteration.
   *
   * @param shouldIncludeHidden - If true, includes all hidden questions with default values
   */
  const getAnswers = (shouldIncludeHidden = false): AnswerObject[] => {
    // Get latest answers directly from the store since hooks are possibly not up-to-date yet
    // https://stackoverflow.com/a/69212617
    const { answers } = store.getState().flows;
    const idsForGroup = getIdsForGroup();

    // Should only include answers for the current group
    const currentGroupAnswers: FlowsQuestionAnswer[] =
      answers?.filter(({ id }) => idsForGroup.includes(id)) || [];

    // Only include hidden questions with default values when requested
    if (shouldIncludeHidden) {
      const allHiddenQuestionAnswers =
        store.getState().flows?.metaData?.allHiddenQuestionAnswers || {};

      // Add answers for all hidden questions with default values
      for (const key in allHiddenQuestionAnswers) {
        if (
          Object.prototype.hasOwnProperty.call(allHiddenQuestionAnswers, key)
        ) {
          const answerArray = allHiddenQuestionAnswers[key];

          if (key.startsWith('_single_')) {
            // Single hidden question - no repetition
            currentGroupAnswers.push(...answerArray);
          } else {
            // Amount-based repetition (existing duplicate logic)
            const amountQuestion = answers?.find(({ id }) => id === key);

            if (amountQuestion) {
              const repetitionCount = Number(amountQuestion.answer);

              for (let index = 0; index < repetitionCount; index++) {
                const answersWithIndex = answerArray.map((answer) => ({
                  ...answer,
                  index,
                }));
                currentGroupAnswers.push(...answersWithIndex);
              }
            }
          }
        }
      }
    }

    const payload = prepareQuestionPayload(currentGroupAnswers);
    return payload;
  };

  /**
   * This function creates a payload from the ansers currently in the store that is filtered and ready to be send to the flows API
   */
  const prepareQuestionPayload = (
    answers: FlowsQuestionAnswer[],
  ): AnswerObject[] => {
    let currentGroupAnswers: FlowsQuestionAnswer[] = [...answers];

    // Should only include for same index if index is defined
    // NOT FOR COMPANY-SCAN

    /** When a flow uses the TABLE component, the duplicate questions shouldn't be shown in the flow (but in a table) */
    const noDuplicatedQuestionFlows = [
      flowIdShortCheckupCompanyNL,
      flowIdShortCheckupCompanyBE,
      flowIdAskaTravelOffer,
      flowIdCitizinsTravel,
    ];

    if (!hasSubGroups(flowId) && !noDuplicatedQuestionFlows.includes(flowId))
      currentGroupAnswers = currentGroupAnswers.filter(
        ({ index }) =>
          index == null ||
          index === currentGroup.index ||
          currentGroup.index == null,
      );

    // Remove groups that have an index and is not greater or equal to 0
    currentGroupAnswers = currentGroupAnswers.filter(
      ({ index }) => index == null || index >= 0,
    );

    // Should not contain _local (This is only used by FE)
    const answerObjects = currentGroupAnswers.map(({ id, answer, index }) => {
      let answerObject: AnswerObject | undefined = { id, answer, index };

      if (
        typeof answer === 'object' &&
        !Array.isArray(answer) &&
        answer != null
      )
        answerObject = {
          ...answerObject,
          answer: { ...answer, _local: undefined },
        };

      if (typeof answer === 'string' && answer === '')
        answerObject = {
          ...answerObject,
          answer: null,
        };

      return answerObject;
    });

    return answerObjects;
  };

  /**
   * Check if we're at the last question of a specific index iteration
   */
  const isLastQuestionOfIndexIteration = (): boolean => {
    const { questionnaire } = store.getState().flows;

    // If this group has duplicate data and an index, check if it's the last question for this index
    if (
      currentGroup.duplicateQuestionData &&
      currentGroup.index !== undefined
    ) {
      const { id: amountQuestionId } = currentGroup.duplicateQuestionData;
      const currentIndex = currentGroup.index;

      // Find the next group position to see if we're transitioning to a different index or different group
      const nextPosition = position + 1;
      if (nextPosition < questionnaire.groups.length) {
        const nextGroup = questionnaire.groups[nextPosition];

        // If next group has different index or different duplicate data, we're at the end of this iteration
        return (
          !nextGroup.duplicateQuestionData ||
          nextGroup.duplicateQuestionData.id !== amountQuestionId ||
          nextGroup.index !== currentIndex
        );
      }
    }

    return false;
  };

  /**
   * Progresses to the next question by doing an API call to the Flows API.
   * If this call fails it will not continue to the next question.
   */
  const nextQuestion = handleSubmit?.(async () => {
    const { questionnaire, metaData } = store.getState().flows;
    const isFinalSubmission = position === questionnaire.groups.length - 1;
    const isEndOfIndexIteration = isLastQuestionOfIndexIteration();

    // Include hidden questions on final submission OR when completing an index iteration
    const shouldIncludeHiddenQuestions =
      isFinalSubmission || isEndOfIndexIteration;
    const currentGroupAnswers = getAnswers(shouldIncludeHiddenQuestions);
    if (currentGroupAnswers.length !== 0 && !isViewOnlyMode) {
      try {
        await mutateAsync({
          flowId,
          sessionId,
          answers: {
            answers: currentGroupAnswers,
          },
        });
      } catch (e) {
        const apiError = e as FlowsAPIAnswerError;

        if (apiError.errors) {
          apiError.errors.forEach(({ message }, index) => {
            if (!toast.isActive(message))
              toast({
                id: message,
                description: (
                  <span dangerouslySetInnerHTML={{ __html: message }} />
                ),
                status: 'error',
                duration: 6000,
                isClosable: true,
                position: 'top-right',
              });
          });
        }

        return console.error(e);
      }
    }

    flowsDispatch(incSubmitCount());
    flowsDispatch(setValidationErrorMessage(''));

    // IF final question, push to outputRoute
    if (position !== questionnaire.groups.length - 1) {
      flowsDispatch(setLastPosition(position + 1));
      history.push(
        setQueryParam(
          `/flows/${flowId}/questions/${sessionId}/${position + 1}`,
          search,
        ),
      );
    } else {
      // Return to output url and set session as inactive
      flowsDispatch(setIsSessionActive(false));
      // TODO: Remove this check and the markFlowAsComplete call
      // This call should happen on the outputUrl itself, or it should be determined by a parameter from the metadata
      if (metaData.shouldComplete)
        await markFlowAsComplete({ conversationId: sessionId });
      history.push(setQueryParam(metaData.outputUrl, search));
    }

    final();
  }, handleOnFormError);

  const previousQuestion = () => {
    const { metaData } = store.getState().flows;

    // Make sure user can always submit
    flowsDispatch(setToastErrorMessage(''));
    flowsDispatch(setValidationErrorMessage(''));

    // IF first question, push to inputRoute
    if (position !== 0) {
      // Push to previous route
      flowsDispatch(setLastPosition(position - 1));
      history.push(
        setQueryParam(
          `/flows/${flowId}/questions/${sessionId}/${position - 1}`,
          search,
        ),
      );
    } else if (metaData.inputUrl) {
      // Return to input url
      history.push(setQueryParam(metaData.inputUrl, search));
    }

    final();
  };

  const saveProgress = async () => {
    const currentGroupAnswers = getAnswers(false); // Never include hidden questions in progress save
    const progressSaveSuccessToadId = 'toastSaveSuccess';
    const progressSaveErrorToadId = 'toastSaveError';
    if (currentGroupAnswers.length !== 0 && !isViewOnlyMode) {
      try {
        await mutateAsync({
          flowId,
          sessionId,
          answers: {
            answers: currentGroupAnswers,
          },
        });
      } catch (e) {
        if (!toast.isActive(progressSaveErrorToadId))
          toast({
            id: progressSaveErrorToadId,
            description: t('FLOWS_PROGRESS_SAVE-FAILURE'),
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'top-right',
          });

        return console.error(e);
      }
    }

    if (!toast.isActive(progressSaveSuccessToadId))
      toast({
        id: progressSaveSuccessToadId,
        description: t('FLOWS_PROGRESS_SAVE-SUCCESS'),
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
  };

  const saveSingleField = (
    questionId: string,
    questionIndex?: number,
  ): void => {
    const answers = store.getState().flows.answers;
    const answerIndex = getIndexOfAnswerForQuestionId(
      answers,
      questionId,
      questionIndex,
    );

    const saveableAnswers = prepareQuestionPayload(
      [answers[answerIndex]].filter((answer) => !!answer),
    );

    if (saveableAnswers.length > 0)
      try {
        mutate({
          flowId,
          sessionId,
          answers: {
            answers: saveableAnswers,
          },
          saveProgress: true,
        });
      } catch (e) {
        const apiError = e as FlowsAPIAnswerError;

        if (apiError.errors) {
          apiError.errors.forEach(({ message }, index) => {
            if (!toast.isActive(message))
              toast({
                id: message,
                description: message,
                status: 'error',
                duration: 6000,
                isClosable: true,
                position: 'top-right',
              });
          });
        }

        return console.error(e);
      }
  };

  const saveFields = async (answers: FlowsQuestionAnswer[]) => {
    const saveableAnswers = prepareQuestionPayload(
      answers.filter((answer) => !!answer),
    );

    if (saveableAnswers.length > 0)
      try {
        await mutateAsync({
          flowId,
          sessionId,
          answers: {
            answers: saveableAnswers,
          },
          saveProgress: true,
        });
      } catch (e) {
        // const apiError = e as FlowsAPIAnswerError;

        // if (apiError.errors) {
        //   apiError.errors.forEach(({ message }, index) => {
        //     if (!toast.isActive(message))
        //       toast({
        //         id: message,
        //         description: message,
        //         status: 'error',
        //         duration: 6000,
        //         isClosable: true,
        //         position: 'top-right',
        //       });
        //   });
        // }

        return console.error(e);
      }
  };

  /**
   * This function should be called when navigation is finished
   */
  const final = () => {
    Userpilot.reload();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saveSingleFieldDebounced, _, flush] = useDebouncedCallback(
    saveSingleField,
    500,
  );

  useEffect(() => flush, [saveSingleFieldDebounced]);
  return {
    nextQuestion,
    previousQuestion,
    saveProgress,
    saveSingleField,
    saveSingleFieldDebounced,
    saveFields,
  };
};
